import { useMemo, useState } from "react";
import { Box, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { num } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import { Badge, Button, Input, NumberInput, useConfirm } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";

interface PackingBoxRow {
  id: string;
  name: string;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
  tareGrams: number;
  sortOrder: number;
  active: boolean;
}

interface BoxForm {
  name: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
  tareGrams: string;
  sortOrder: string;
  active: boolean;
}

function emptyForm(): BoxForm {
  return { name: "", lengthCm: "", widthCm: "", heightCm: "", tareGrams: "0", sortOrder: "0", active: true };
}

function rowToForm(row: PackingBoxRow): BoxForm {
  return {
    name: row.name,
    lengthCm: String(row.lengthCm),
    widthCm: String(row.widthCm),
    heightCm: String(row.heightCm),
    tareGrams: String(row.tareGrams),
    sortOrder: String(row.sortOrder),
    active: row.active,
  };
}

function dimsLabel(row: PackingBoxRow): string {
  return `${num(row.lengthCm)} × ${num(row.widthCm)} × ${num(row.heightCm)} cm`;
}

export function PackingBoxes() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<PackingBoxRow | null>(null);
  const [form, setForm] = useState<BoxForm>(emptyForm());

  const canWrite = can("orders.fulfil");

  const listQuery = useQuery({
    queryKey: ["packing-boxes"],
    queryFn: () => api<{ items: PackingBoxRow[] }>("/packing-boxes"),
  });

  const rows = listQuery.data?.items ?? [];

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: Record<string, unknown> }) => {
      if (payload.id) {
        return api(`/packing-boxes/${payload.id}`, { method: "PATCH", body: payload.body });
      }
      return api("/packing-boxes", { method: "POST", body: payload.body });
    },
    onSuccess: () => {
      toast("success", editing ? "Packing box updated" : "Packing box created");
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["packing-boxes"] });
      void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklist"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/packing-boxes/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["packing-boxes"] });
      void queryClient.invalidateQueries({ queryKey: ["fulfilment-picklist"] });
      toast("success", "Packing box removed");
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Delete failed"),
  });

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
    setForm(emptyForm());
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  }

  function openEdit(row: PackingBoxRow) {
    setEditing(row);
    setForm(rowToForm(row));
    setEditorOpen(true);
  }

  function save() {
    const lengthCm = Number.parseFloat(form.lengthCm);
    const widthCm = Number.parseFloat(form.widthCm);
    const heightCm = Number.parseFloat(form.heightCm);
    const tareGrams = Number.parseInt(form.tareGrams, 10);
    const sortOrder = Number.parseInt(form.sortOrder, 10);
    if (!form.name.trim()) {
      toast("error", "Enter a name");
      return;
    }
    if (!Number.isFinite(lengthCm) || lengthCm <= 0) {
      toast("error", "Enter valid length");
      return;
    }
    if (!Number.isFinite(widthCm) || widthCm <= 0) {
      toast("error", "Enter valid width");
      return;
    }
    if (!Number.isFinite(heightCm) || heightCm <= 0) {
      toast("error", "Enter valid height");
      return;
    }
    saveMutation.mutate({
      id: editing?.id,
      body: {
        name: form.name.trim(),
        lengthCm,
        widthCm,
        heightCm,
        tareGrams: Number.isFinite(tareGrams) ? tareGrams : 0,
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
        active: form.active,
      },
    });
  }

  useBottomBarActions(() =>
    canWrite ? [{ id: "new-box", label: "New box", icon: Plus, onClick: openCreate }] : [],
  );

  const columns: Column<PackingBoxRow>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        sortable: true,
        sortValue: (r) => r.name,
        render: (r) => <span className="font-medium">{r.name}</span>,
      },
      {
        key: "dims",
        header: "Dimensions",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.lengthCm * r.widthCm * r.heightCm,
        exportValue: dimsLabel,
        render: (r) => <span className="text-xs text-white/60">{dimsLabel(r)}</span>,
      },
      {
        key: "tare",
        header: "Tare (g)",
        className: "text-right",
        hideSm: true,
        sortable: true,
        sortValue: (r) => r.tareGrams,
        render: (r) => <span className="text-xs">{num(r.tareGrams)}</span>,
      },
      {
        key: "sort",
        header: "Order",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.sortOrder,
        render: (r) => <span className="text-xs text-white/50">{r.sortOrder}</span>,
      },
      {
        key: "active",
        header: "Status",
        sortable: true,
        sortValue: (r) => (r.active ? 1 : 0),
        render: (r) => <Badge color={r.active ? "green" : "gray"}>{r.active ? "Active" : "Inactive"}</Badge>,
      },
    ],
    [],
  );

  return (
    <ListPageShell>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={listQuery.isLoading}
        exportFilename="packing-boxes"
        onRowClick={canWrite ? openEdit : undefined}
        activeKey={editing?.id}
        empty={{
          icon: Box,
          title: "No packing boxes yet",
          hint: "Add standard box sizes for the pack workflow.",
        }}
      />

      <RightSidebarPanel
        open={editorOpen}
        title={editing ? "Edit packing box" : "New packing box"}
        onClose={closeEditor}
        footer={
          canWrite && (
            <div className="flex justify-end gap-2">
              {editing && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={deleteMutation.isPending}
                  onClick={() =>
                    confirm({
                      title: "Remove packing box?",
                      message: "Inactive boxes stay on historical parcels. Boxes in use are deactivated instead of deleted.",
                      confirmLabel: "Remove",
                      danger: true,
                      action: () => deleteMutation.mutate(editing.id),
                    })
                  }
                >
                  Remove
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={closeEditor}>
                Cancel
              </Button>
              <Button size="sm" disabled={saveMutation.isPending} onClick={save}>
                Save
              </Button>
            </div>
          )
        }
      >
        <div className="sidebar-form">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Small mailer"
          />
          <div className="form-row-3">
            <NumberInput
              label="Length (cm)"
              value={form.lengthCm}
              onValueChange={(value) => setForm((f) => ({ ...f, lengthCm: value }))}
            />
            <NumberInput
              label="Width (cm)"
              value={form.widthCm}
              onValueChange={(value) => setForm((f) => ({ ...f, widthCm: value }))}
            />
            <NumberInput
              label="Height (cm)"
              value={form.heightCm}
              onValueChange={(value) => setForm((f) => ({ ...f, heightCm: value }))}
            />
          </div>
          <div className="form-row-2">
            <NumberInput
              label="Tare (g)"
              hint="Empty box weight"
              value={form.tareGrams}
              onValueChange={(value) => setForm((f) => ({ ...f, tareGrams: value }))}
            />
            <NumberInput
              label="Sort order"
              value={form.sortOrder}
              onValueChange={(value) => setForm((f) => ({ ...f, sortOrder: value }))}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              className="rounded border-line bg-surface"
            />
            Active
          </label>
        </div>
      </RightSidebarPanel>

      {dialog}
    </ListPageShell>
  );
}
